import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import './AIAssistedTemplate.scss';
import {
  ButtonFlavor, ButtonSize, ButtonType, ExButton, ExIcon, ExLoader, ExTextarea,
  IconSize,
  LoaderSize,
  LoaderVariant,
} from '@boomi/exosphere';
import { useBoomiAgentFoundationContext } from '@context/BoomiAgentFoundationContext';
import { generateAIAssistedAgentTemplate, handleError, transformApiResponseToWizardData } from '@services/agent-foundation/agent-foundation-api-service';
import agentTemplates from '@common/data/agent-foundation/agentTemplates.json';

function AIAssistedTemplate({ goBack, setShowBuilderDialog, initialGoal }:any) {
  // `initialGoal` seeds the field when Agentstudio was opened from a Boomi
  // Concierge deeplink carrying an agent-authored Goal Description, so the user
  // does not have to copy it across by hand. Seeded as the initial state rather
  // than through an effect: the value never changes for the life of the dialog,
  // and an effect would fight anything the user then types.
  const [objectiveTextAreaValue, setObjectiveTextAreaValue] = useState<string>(initialGoal ?? '');
  const {
    setWizardData, setCreateAgentMethod, createAbortController, abortRequests,
    setBuilderDialogTitle,
  } = useBoomiAgentFoundationContext();
  const [isLoading, setIsLoading] = useState(false);
  const [t] = useTranslation('common');
  const navigate = useNavigate();

  // Cleanup function to abort any ongoing API calls
  useEffect(() => () => {
    abortRequests();
  }, []); // Only run on component unmount

  // Function to cancel ongoing API calls
  const cancelApiCall = () => {
    abortRequests();
    setIsLoading(false);
  };

  // Process template data into array format for rendering
  const getTemplateArray = () => {
    const templates: Array<{ id: string; short_desc: string;
      objective: string; }> = [];

    // Add Public Templates
    Object.entries(agentTemplates.Public_Templates).forEach(([key, template]) => {
      templates.push({
        id: key,
        short_desc: template.short_desc,
        objective: template.objective,
      });
    });

    // Add Enterprise Templates
    Object.entries(agentTemplates.Enterprise_Templates).forEach(([key, template]) => {
      templates.push({
        id: key,
        short_desc: template.short_desc,
        objective: template.objective,
      });
    });

    return templates;
  };

  const templates = getTemplateArray();

  // Create pills click handler
  const handlePillClick = (objective: string) => {
    setObjectiveTextAreaValue(objective);
  };

  // Group templates into maximum 2 rows
  const templateRows = [];
  const itemsPerRow = Math.ceil(templates.length - 4 / 2); // Calculate items per row for max 2 rows
  for (let i = 0; i < templates.length - 4; i += itemsPerRow) {
    templateRows.push(templates.slice(i, i + itemsPerRow));
  }

  const handleObjectiveValue = (e: any) => {
    setObjectiveTextAreaValue(e.target.value);
  };

  const generateAgent = () => {
    // Cancel any existing API call and create new controller
    abortRequests();
    const controller = createAbortController();
    const { signal } = controller;

    setIsLoading(true);

    generateAIAssistedAgentTemplate(objectiveTextAreaValue, signal)
      .then((res: any) => {
        // Check if the request was aborted
        if (signal.aborted) {
          return;
        }

        if (res.success === false) {
          setIsLoading(false);
          handleError(res, 'Failed to generate an agent');
          return;
        }

        setIsLoading(false);
        if (res.data) {
          const updatedWizardData = transformApiResponseToWizardData(res.data);
          setWizardData(updatedWizardData);
        }
        setShowBuilderDialog(false);
        setBuilderDialogTitle(t('agent_builder_dialog_title_agent_designer'));
        navigate('/agent-designer');
      })
      .catch((error) => {
        // Don't show error if request was aborted
        if (error.name === 'AbortError' || signal.aborted) {
          setBuilderDialogTitle(t('agent_builder_dialog_title_agent_designer'));
          console.log('API call was cancelled');
          return;
        }

        setIsLoading(false);
        handleError({ success: false, message: error.message }, 'Failed to generate an agent');
      });
  };

  return (
    <>
      <div className="ai-assisted-dialog-container">
        {isLoading && (
        <div className="ai-agent-loader-container">
          <ExLoader size={LoaderSize.MEDIUM} label={t('agent_builder_wizard_creating_agent_message')} variant={LoaderVariant.SPINNER} />
        </div>
        )}
        {!isLoading && (
        <>
          <div className="ai-assisted-description">
            {t('ai_assisted_dialog_description')}
          </div>
          <ExTextarea
            placeholder={t('ai_agent_garden_agent_goal')}
            data-testid="ai_assisted_agent_goal_textarea"
            resize="none"
            name={t('textarea')}
            value={objectiveTextAreaValue}
            onChange={handleObjectiveValue}
            onInput={handleObjectiveValue}
            rows={12}
            maxlength={5000}
            label={t('goal')}
            infoText={t('ai_agent_build_agent_objective')}
            required
            className="objective-text"
          />
        </>
        )}
      </div>

      {/* Suggestion Pills */}
      {!isLoading && (
        <div className="suggestion-pills-section ai-assisted-pills">
          <div className="suggestions-title">Here are some examples to try</div>
          <div className="suggestion-pills-grid">
            {templateRows.map((row) => (
              <div key={row.map((template) => template.id).join('-')} className="suggestion-pill-row">
                {row.map((template) => (
                  <button
                    key={template.id}
                    type="button"
                    className="suggestion-pill"
                    onClick={() => handlePillClick(template.objective)}
                    data-testid={`ai-assisted-suggestion-pill-${template.id.toLowerCase()}`}
                  >
                    <span className="pill-text">{template.short_desc}</span>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {!isLoading && (
      <>
        <ExButton
          data-testid="ai_assisted_dialog_back_button"
          flavor={ButtonFlavor.BRANDED}
          type={ButtonType.SECONDARY}
          size={ButtonSize.DEFAULT}
          className="ai-back-button"
          disabled={isLoading}
          onClick={() => {
            cancelApiCall(); // Cancel any ongoing API calls
            goBack();
          }}
          slot="footer"
        >
          {t('ai_assisted_dialog_back_button')}
        </ExButton>
        <ExButton
          flavor={ButtonFlavor.BRANDED}
          type={ButtonType.PRIMARY}
          size={ButtonSize.DEFAULT}
          disabled={isLoading || objectiveTextAreaValue?.length === 0}
          className="ai-build-button"
          data-testid="ai_assisted_dialog_build_agent_btn"
          onClick={() => {
            setCreateAgentMethod('Built-with-AI');
            generateAgent();
          }}
          slot="footer"
        >
          <ExIcon
            className="button-icon"
            icon="Auto arrange"
            hideBrowserTooltip
            size={IconSize.S}
          />
          {t('ai_assisted_dialog_build_button')}
        </ExButton>
      </>
      )}
    </>
  );
}

export default AIAssistedTemplate;
